import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import axios from 'axios'
import { Trophy, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Checkbox } from '../../ui/checkbox'
import { requestCreator } from '../../../utilities/requests'
import { SPORT_OPTIONS, getSportLabel } from '../../../utilities/sportOptions'

const { get: getCurriculums, cancel: cancelCurriculumsRequest } = requestCreator()

function SportCurriculumSection({
  facilityId,
  sport,
  curriculumId,
  curriculumName,
  supportedSkillLevels,
  onSportChange,
  onCurriculumChange,
  onSkillLevelsChange
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [curriculums, setCurriculums] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [localSport, setLocalSport] = useState(sport || '')
  const [localCurriculumId, setLocalCurriculumId] = useState(curriculumId)
  const [localSkillLevelIds, setLocalSkillLevelIds] = useState(
    supportedSkillLevels?.map((sl) => sl.id) || []
  )

  useEffect(() => {
    setLocalSport(sport || '')
    setLocalCurriculumId(curriculumId)
    setLocalSkillLevelIds(supportedSkillLevels?.map((sl) => sl.id) || [])
  }, [sport, curriculumId, supportedSkillLevels])

  useEffect(() => {
    if (isExpanded && facilityId) {
      loadCurriculums()
    }
    return cancelCurriculumsRequest
  }, [isExpanded, facilityId])

  function loadCurriculums() {
    setIsLoading(true)
    getCurriculums(`/api/facilities/${facilityId}/curriculums`, {
      params: { include: 'skill_levels' }
    })
      .then((data) => {
        setCurriculums(data)
        setIsLoading(false)
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error('Error loading curriculums:', error)
        }
        setIsLoading(false)
      })
  }

  const selectedCurriculum = curriculums.find((c) => c.id === localCurriculumId)
  const filteredCurriculums = curriculums.filter((c) => c.sport === localSport)

  function handleSportChange(newSport) {
    setLocalSport(newSport)
    setLocalCurriculumId(null)
    setLocalSkillLevelIds([])
    onSportChange(newSport)
    onCurriculumChange(null, null)
    onSkillLevelsChange([])
  }

  function handleCurriculumChange(newCurriculumId) {
    const curriculum = curriculums.find((c) => c.id === parseInt(newCurriculumId))
    setLocalCurriculumId(parseInt(newCurriculumId))
    setLocalSkillLevelIds([])
    onCurriculumChange(parseInt(newCurriculumId), curriculum?.name)
    onSkillLevelsChange([])
  }

  function handleSkillLevelToggle(skillLevelId, checked) {
    let newIds
    if (checked) {
      newIds = [...localSkillLevelIds, skillLevelId]
    } else {
      newIds = localSkillLevelIds.filter((id) => id !== skillLevelId)
    }
    setLocalSkillLevelIds(newIds)

    const selectedLevels = selectedCurriculum?.skillLevels?.filter((sl) => newIds.includes(sl.id)) || []
    onSkillLevelsChange(selectedLevels)
  }

  const displaySport = getSportLabel(localSport) || 'Not set'
  const displayCurriculum = curriculumName || 'Not set'
  const displayLevels =
    supportedSkillLevels?.length > 0 ? supportedSkillLevels.map((sl) => sl.name).join(', ') : 'All levels'

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex items-center justify-between w-full px-4 py-3 text-left"
      >
        <div className="flex items-center space-x-2">
          <Trophy className="w-5 h-5 text-purple-600" />
          <h3 className="text-base font-semibold text-gray-900">Sport & Curriculum</h3>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {!isExpanded && (
        <div className="px-4 pb-3 text-sm text-gray-600">
          <span className="font-medium">{displaySport}</span>
          {localCurriculumId && (
            <>
              {' · '}
              <span>{displayCurriculum}</span>
            </>
          )}
          {supportedSkillLevels?.length > 0 && (
            <>
              {' · '}
              <span>{displayLevels}</span>
            </>
          )}
        </div>
      )}

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200">
          <div className="mt-4">
            <Label className="mb-2 block text-sm font-medium">Sport Type</Label>
            <Select value={localSport} onValueChange={handleSportChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a sport" />
              </SelectTrigger>
              <SelectContent>
                {SPORT_OPTIONS.map((sportOption) => (
                  <SelectItem key={sportOption.value} value={sportOption.value}>
                    {sportOption.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {localSport && (
            <div>
              <Label className="mb-2 block text-sm font-medium">Curriculum</Label>
              {isLoading ? (
                <p className="text-sm text-gray-500">Loading curriculums...</p>
              ) : filteredCurriculums.length === 0 ? (
                <p className="text-sm text-gray-500">No curriculums available for this sport.</p>
              ) : (
                <Select
                  value={localCurriculumId?.toString() || ''}
                  onValueChange={handleCurriculumChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a curriculum" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCurriculums.map((curriculum) => (
                      <SelectItem key={curriculum.id} value={curriculum.id.toString()}>
                        {curriculum.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {selectedCurriculum && selectedCurriculum.skillLevels?.length > 0 && (
            <div>
              <Label className="mb-2 block text-sm font-medium">Skill Levels for this Class</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3 bg-gray-50">
                {selectedCurriculum.skillLevels.map((level) => (
                  <div key={level.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-level-${level.id}`}
                      checked={localSkillLevelIds.includes(level.id)}
                      onCheckedChange={(checked) => handleSkillLevelToggle(level.id, checked)}
                    />
                    <Label htmlFor={`edit-level-${level.id}`} className="font-normal cursor-pointer text-sm">
                      {level.name}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Leave all unchecked to allow all skill levels in this class.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

SportCurriculumSection.propTypes = {
  facilityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  sport: PropTypes.string,
  curriculumId: PropTypes.number,
  curriculumName: PropTypes.string,
  supportedSkillLevels: PropTypes.array,
  onSportChange: PropTypes.func.isRequired,
  onCurriculumChange: PropTypes.func.isRequired,
  onSkillLevelsChange: PropTypes.func.isRequired
}

export default SportCurriculumSection

